import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Search, Calendar, PackageOpen, CheckCircle2, Clock, Flame, Beef, Wheat, Droplet, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrders } from '../hooks/useOrders';
import { usePinto } from '../hooks/usePinto';
import { usePintoSchedule } from '../hooks/usePintoSchedule';
import { formatThaiDateShort } from '../utils/dateFormat';
import { calculateExactPintoMeals } from '../utils/pintoCalculator';
import Skeleton from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import './DashboardPage.css';

const TABS = [
  { id: 'overview', label: 'ภาพรวม' },
  { id: 'pinto', label: 'มื้อปิ่นโต' },
  { id: 'alacarte', label: 'สั่งทั่วไป' }
];

const COLORS = {
  pro: '#4f46e5', // Indigo
  carb: '#10b981', // Emerald
  fat: '#f59e0b' // Amber
};

const DashboardPage = () => {
  const { member } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const formatDate = (date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    return {
      start: formatDate(start),
      end: formatDate(end)
    };
  });
  const [mealDisplayMode, setMealDisplayMode] = useState('current');
  
  // Data Fetching
  const { normalOrders, loading: ordersLoading } = useOrders(member?.phone, dateRange.start, dateRange.end);
  const { activePackage, packages, metrics, loading: pintoLoading } = usePinto(member);
  const { schedules, loading: schedulesLoading } = usePintoSchedule(activePackage?.id);
  
  const allTimeTotalMeals = useMemo(() => {
    if (!packages || packages.length === 0) return 0;
    return packages.reduce((sum, pkg) => {
      const pkgMetrics = calculateExactPintoMeals(pkg);
      return sum + (pkgMetrics?.totalMeals || 0);
    }, 0);
  }, [packages]);

  const [searchQueryPinto, setSearchQueryPinto] = useState('');
  const [selectedCategoryPinto, setSelectedCategoryPinto] = useState('all');
  const [searchQueryALaCarte, setSearchQueryALaCarte] = useState('');

  const pintoCategories = useMemo(() => {
    if (!schedules) return ['all'];
    const cats = new Set();
    schedules.forEach(s => {
      if (!s.is_extra_order && s.menu_items?.category) {
        cats.add(s.menu_items.category);
      }
    });
    return ['all', ...Array.from(cats)];
  }, [schedules]);

  const getFirstName = (fullName) => {
    if (!fullName) return 'ลูกค้า';
    return fullName.split(' ')[0];
  };

  const handleDateChange = (e, field) => {
    setDateRange(prev => ({ ...prev, [field]: e.target.value }));
  };

  const resetToToday = () => {
    const today = new Date().toISOString().split('T')[0];
    setDateRange({ start: today, end: today });
  };

  const resetToThisMonth = () => {
    const now = new Date();
    // Use local time for start/end of month to prevent timezone issues
    const year = now.getFullYear();
    const month = now.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Format as YYYY-MM-DD manually to keep local timezone
    const format = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    
    setDateRange({ start: format(firstDay), end: format(lastDay) });
  };

  const resetToAll = () => {
    setDateRange({ start: '', end: '' });
  };


  const calculateAdjustedMacros = (menuItems, notes) => {
    if (!menuItems) return { cal: 0, pro: 0, carb: 0, fat: 0 };
    let cal = menuItems.calories || 0;
    let pro = menuItems.protein || 0;
    let carb = menuItems.carbs || 0;
    let fat = menuItems.fat || 0;

    if (notes && notes.includes('[ไม่รับข้าว]')) {
      cal = Math.max(0, cal - 108);
      carb = Math.max(5, carb - 23);
    }

    return { cal, pro, carb, fat };
  };

  // ----------------------------------------------------
  // Derived Data & Calculations
  // ----------------------------------------------------
  
  // 1. Group Pinto Schedules (Exclude extra orders)
  const { groupedSchedules, pintoMacros, sortedPintoDates, extraSchedules } = useMemo(() => {
    if (!schedules || schedules.length === 0) return { groupedSchedules: {}, pintoMacros: { cal: 0, pro: 0, carb: 0, fat: 0 }, sortedPintoDates: [], extraSchedules: [] };

    let filtered = schedules;
    if (dateRange.start) {
      filtered = filtered.filter(s => new Date(s.delivery_date) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      const end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(s => new Date(s.delivery_date) <= end);
    }

    const getMealWeight = (type) => {
      if (type === 'breakfast' || type === 'meal_1') return 1;
      if (type === 'lunch' || type === 'meal_2') return 2;
      if (type === 'dinner' || type === 'meal_3') return 3;
      return 99;
    };

    const pintoOnly = [...filtered].filter(s => !s.is_extra_order).sort((a, b) => {
      const dateDiff = new Date(a.delivery_date) - new Date(b.delivery_date);
      if (dateDiff !== 0) return dateDiff;
      return getMealWeight(a.meal_type) - getMealWeight(b.meal_type);
    });
    pintoOnly.forEach((s, idx) => {
      s.global_meal_number = idx + 1;
    });

    const extras = filtered.filter(s => s.is_extra_order);

    const searchCatFiltered = pintoOnly.filter(s => {
      const searchMatch = searchQueryPinto ? s.menu_items?.name?.toLowerCase().includes(searchQueryPinto.toLowerCase()) : true;
      const catMatch = selectedCategoryPinto === 'all' || s.menu_items?.category === selectedCategoryPinto;
      return searchMatch && catMatch;
    });

    const grouped = searchCatFiltered.reduce((acc, curr) => {
      const date = curr.delivery_date;
      if (!acc[date]) acc[date] = { items: [], macros: { cal: 0, pro: 0, carb: 0, fat: 0 } };
      acc[date].items.push(curr);
      if (curr.menu_items) {
        const qty = curr.quantity || 1;
        const adj = calculateAdjustedMacros(curr.menu_items, curr.notes);
        acc[date].macros.cal += adj.cal * qty;
        acc[date].macros.pro += adj.pro * qty;
        acc[date].macros.carb += adj.carb * qty;
        acc[date].macros.fat += adj.fat * qty;
      }
      return acc;
    }, {});

    const totalMacros = pintoOnly.reduce((acc, curr) => {
      if (curr.menu_items) {
        const qty = curr.quantity || 1;
        const adj = calculateAdjustedMacros(curr.menu_items, curr.notes);
        acc.cal += adj.cal * qty;
        acc.pro += adj.pro * qty;
        acc.carb += adj.carb * qty;
        acc.fat += adj.fat * qty;
      }
      return acc;
    }, { cal: 0, pro: 0, carb: 0, fat: 0 });

    const sortedDates = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a));

    return { groupedSchedules: grouped, pintoMacros: totalMacros, sortedPintoDates: sortedDates, extraSchedules: extras };
  }, [schedules, dateRange, searchQueryPinto, selectedCategoryPinto]);

  // 2. Normal Orders Macros
  const normalMacros = useMemo(() => {
    return normalOrders.reduce((acc, o) => {
      acc.cal += (o.calories || 0);
      acc.pro += (o.protein || 0);
      acc.carb += (o.carbs || 0);
      acc.fat += (o.fat || 0);
      return acc;
    }, { cal: 0, pro: 0, carb: 0, fat: 0 });
  }, [normalOrders]);

  // 3. Combined Total Macros
  const totalMacros = {
    cal: pintoMacros.cal + normalMacros.cal,
    pro: pintoMacros.pro + normalMacros.pro,
    carb: pintoMacros.carb + normalMacros.carb,
    fat: pintoMacros.fat + normalMacros.fat
  };

  const macroChartData = [
    { name: 'Protein (g)', value: totalMacros.pro, color: COLORS.pro },
    { name: 'Carbs (g)', value: totalMacros.carb, color: COLORS.carb },
    { name: 'Fat (g)', value: totalMacros.fat, color: COLORS.fat }
  ].filter(d => d.value > 0);

  const totalSpent = normalOrders.reduce((sum, o) => sum + (Number(o.total_amount) || 0), 0);
  const totalItemsCount = normalOrders.length + (schedules?.filter(s => !s.is_extra_order)?.length || 0) + (extraSchedules?.length || 0);

  const isScheduleCompleted = (schedule) => {
    if (schedule.kitchen_status === 'completed') return true;
    const deliveryDate = new Date(schedule.delivery_date);
    const now = new Date();
    
    const dDay = new Date(deliveryDate);
    dDay.setHours(0, 0, 0, 0);
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    if (dDay < today) return true;
    if (dDay > today) return false;

    // Today: check by time
    // Pinto meals are delivered together in a single round.
    // Assuming the round is finished by 13:00 (1 PM).
    const currentHour = now.getHours();
    if (currentHour >= 13) {
      return true;
    }
    
    // If before 13:00, consider them still pending
    return false;
  };

  const actualConsumed = useMemo(() => {
    if (!schedules) return 0;
    return schedules.filter(s => !s.is_extra_order && isScheduleCompleted(s)).length;
  }, [schedules]);

  const actualRemaining = metrics ? Math.max(0, metrics.totalMeals - actualConsumed) : 0;

  const allTimeConsumedMeals = useMemo(() => {
    if (!packages || packages.length === 0) return 0;
    let sum = 0;
    packages.forEach(pkg => {
      if (activePackage && pkg.id === activePackage.id) {
        sum += actualConsumed;
      } else {
        const m = calculateExactPintoMeals(pkg);
        sum += (m?.consumedMeals || 0);
      }
    });
    return sum;
  }, [packages, activePackage, actualConsumed]);

  const currentProgress = metrics && metrics.totalMeals > 0 
    ? Math.min(100, (actualConsumed / metrics.totalMeals) * 100) 
    : 0;
    
  const allTimeProgress = allTimeTotalMeals > 0 
    ? Math.min(100, (allTimeConsumedMeals / allTimeTotalMeals) * 100) 
    : 0;

  const filteredALaCarte = useMemo(() => {
    let extras = extraSchedules || [];
    let normals = normalOrders || [];

    if (searchQueryALaCarte) {
      const q = searchQueryALaCarte.toLowerCase();
      extras = extras.filter(ex => (ex.menu_items?.name || '').toLowerCase().includes(q));
      normals = normals.filter(o => (o.menu_name || '').toLowerCase().includes(q));
    }
    
    const combined = [
      ...extras.map(e => ({ ...e, _type: 'extra' })),
      ...normals.map(n => ({ ...n, _type: 'normal' }))
    ].sort((a, b) => new Date(b.delivery_date) - new Date(a.delivery_date));

    return combined;
  }, [extraSchedules, normalOrders, searchQueryALaCarte]);

  // ----------------------------------------------------
  // Render Helpers
  // ----------------------------------------------------

  const renderOverviewTab = () => (
    <motion.div 
      className="dashboard-overview-tab"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <div className="bento-grid">
        {/* Main Chart Box */}
        <div className="bento-card span-2 highlight">
          <div className="card-header-flex">
             <div className="header-title-group">
               <Flame size={20} className="text-brand"/>
               <span className="stat-title" style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-primary)' }}>สัดส่วนโภชนาการรวมสะสม</span>
             </div>
          </div>
          <div className="macro-chart-container">
            {macroChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={macroChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={85}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                  >
                    {macroChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value) => [`${value}g`, '']}
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="empty-chart">ไม่มีข้อมูลโภชนาการในช่วงเวลานี้</div>
            )}
            <div className="macro-legend-group">
              <div className="macro-stat">
                <div className="macro-icon-box bg-red"><Flame size={16}/></div>
                <div className="macro-info">
                  <span className="macro-val">{totalMacros.cal}</span>
                  <span className="macro-lbl">Kcal</span>
                </div>
              </div>
              <div className="macro-stat">
                <div className="macro-icon-box bg-indigo"><Beef size={16}/></div>
                <div className="macro-info">
                  <span className="macro-val">{totalMacros.pro}g</span>
                  <span className="macro-lbl" style={{color: COLORS.pro}}>Protein</span>
                </div>
              </div>
              <div className="macro-stat">
                <div className="macro-icon-box bg-emerald"><Wheat size={16}/></div>
                <div className="macro-info">
                  <span className="macro-val">{totalMacros.carb}g</span>
                  <span className="macro-lbl" style={{color: COLORS.carb}}>Carbs</span>
                </div>
              </div>
              <div className="macro-stat">
                <div className="macro-icon-box bg-amber"><Droplet size={16}/></div>
                <div className="macro-info">
                  <span className="macro-val">{totalMacros.fat}g</span>
                  <span className="macro-lbl" style={{color: COLORS.fat}}>Fat</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Small Stat Cards */}
        <div className="bento-card">
          <div className="card-header-flex">
            <div className="header-title-group">
              <ShoppingBag size={20} className="text-brand"/>
              <span className="stat-title">จำนวนออเดอร์</span>
            </div>
          </div>
          <div className="stat-main">{totalItemsCount} <span className="stat-unit">รายการ</span></div>
          <div className="order-breakdown">
            <div className="breakdown-item">
              <PackageOpen size={14} className="text-brand"/> 
              <span>ปิ่นโต {schedules?.filter(s => !s.is_extra_order)?.length || 0}</span>
            </div>
            <div className="breakdown-item">
              <ShoppingBag size={14} style={{color: 'var(--color-warning)'}}/> 
              <span>ทั่วไป {normalOrders.length + (extraSchedules?.length || 0)}</span>
            </div>
          </div>
        </div>

        <div className="bento-card">
          <div className="card-header-flex">
            <div className="header-title-group">
              <CheckCircle2 size={20} className="text-brand"/>
              <span className="stat-title">รับปิ่นโตแล้ว</span>
            </div>
            <select 
              className="meal-display-select"
              value={mealDisplayMode}
              onChange={(e) => setMealDisplayMode(e.target.value)}
            >
              <option value="current">แพ็กปัจจุบัน</option>
              <option value="all-time">รวมทั้งหมด</option>
            </select>
          </div>
          
          <AnimatePresence mode="wait">
            <motion.div
              key={mealDisplayMode}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
            >
              {mealDisplayMode === 'current' ? (
                <>
                  <div className="stat-main">{actualConsumed} <span className="stat-unit">มื้อ</span></div>
                  <p className="stat-desc">เหลืออีก {actualRemaining} มื้อ จาก {metrics?.totalMeals || 0} มื้อ</p>
                  <div className="mini-progress-track">
                    <motion.div 
                      className="mini-progress-fill" 
                      initial={{width: 0}} 
                      animate={{width: `${currentProgress}%`}} 
                      transition={{duration: 0.8, ease: "easeOut"}}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="stat-main">{allTimeConsumedMeals} <span className="stat-unit">มื้อ</span></div>
                  <p className="stat-desc">จากทั้งหมด {packages?.length || 0} แพ็กเกจ ({allTimeTotalMeals} มื้อ)</p>
                  <div className="mini-progress-track">
                    <motion.div 
                      className="mini-progress-fill" 
                      initial={{width: 0}} 
                      animate={{width: `${allTimeProgress}%`}} 
                      transition={{duration: 0.8, ease: "easeOut"}}
                    />
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );

  const renderPintoTab = () => (
    <motion.div 
      className="dashboard-pinto-tab"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="filter-controls pinto-dash-filters">
        <div className="search-input-wrapper">
          <Search size={16} className="search-icon" />
          <input 
            type="text" 
            placeholder="ค้นหาชื่อเมนู..." 
            value={searchQueryPinto}
            onChange={(e) => setSearchQueryPinto(e.target.value)}
            className="menu-search-input"
          />
        </div>
        {pintoCategories.length > 1 && (
          <div className="category-select-wrapper">
            <select 
              value={selectedCategoryPinto} 
              onChange={(e) => setSelectedCategoryPinto(e.target.value)}
              className="category-select"
            >
              <option value="all">ทุกหมวดหมู่</option>
              {pintoCategories.filter(c => c !== 'all').map(c => (
                <option key={c} value={c}>{c === 'main' ? 'เมนูหลัก' : c}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {schedulesLoading ? (
        <div className="timeline-loading"><Skeleton height="80px" radius="16px" /><Skeleton height="80px" radius="16px" /></div>
      ) : sortedPintoDates.length > 0 ? (
        <div className="meal-history-list">
          {sortedPintoDates.map((date, i) => {
            const dayGroup = groupedSchedules[date];
            return (
              <div key={date} className="daily-meal-group">
                <div className="daily-meal-header">
                  <span className="delivery-date">{formatThaiDateShort(date)}</span>
                  <div className="macros-row small">
                    <span className="macro cal">{dayGroup.macros.cal} Kcal</span>
                    <span className="macro pro">P:{dayGroup.macros.pro}g</span>
                    <span className="macro carb">C:{dayGroup.macros.carb}g</span>
                    <span className="macro fat">F:{dayGroup.macros.fat}g</span>
                  </div>
                </div>
                
                <div className="daily-meal-cards">
                  {dayGroup.items.slice().reverse().map((schedule, j) => (
                    <motion.div 
                      key={schedule.id || j}
                      className="meal-history-card"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-20px" }}
                      transition={{ duration: 0.4 }}
                    >
                      <div className="meal-card-content">
                        <div className="meal-card-header">
                          <div className="meal-title-group">
                            <span className="meal-name-main">{schedule.menu_items?.name || 'เมนูที่เลือก'} {schedule.quantity > 1 && `(x${schedule.quantity})`}</span>
                            {schedule.menu_items?.category && (
                              <span className="category-badge">
                                {schedule.menu_items.category === 'main' ? 'เมนูหลัก' : schedule.menu_items.category}
                              </span>
                            )}
                            {schedule.notes && schedule.notes.includes('[ไม่รับข้าว]') && (
                              <span className="category-badge" style={{ backgroundColor: '#fee2e2', color: '#b91c1c' }}>
                                ไม่รับข้าว
                              </span>
                            )}
                          </div>
                          <span className={`delivery-status ${isScheduleCompleted(schedule) ? 'success' : 'pending'}`}>
                            {isScheduleCompleted(schedule) ? 'ส่งสำเร็จ' : 'รอจัดส่ง'}
                          </span>
                        </div>
                        
                        <div className="meal-type-row">
                          <span className="meal-type-label">
                            มื้อที่ {schedule.global_meal_number} {schedule.meal_type === 'lunch' ? '(มื้อกลางวัน)' : schedule.meal_type === 'dinner' ? '(มื้อเย็น)' : ''}
                          </span>
                        </div>
                        
                        {schedule.menu_items && (() => {
                          const adj = calculateAdjustedMacros(schedule.menu_items, schedule.notes);
                          return (
                            <div className="macros-row mt-2">
                              <span className="macro cal">{adj.cal} Kcal</span>
                              <span className="macro pro">P: {adj.pro}g</span>
                              <span className="macro carb">C: {adj.carb}g</span>
                              <span className="macro fat">F: {adj.fat}g</span>
                            </div>
                          );
                        })()}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState icon={<PackageOpen size={48} />} title="ไม่มีประวัติปิ่นโต" description="ไม่พบตารางการจัดส่งในช่วงเวลานี้" />
      )}
    </motion.div>
  );

  const renderALaCarteTab = () => {
    const getTitle = (name) => {
      if (!name) return 'ไม่มีชื่อเมนู';
      let title = name.split('\n')[0];
      return title.replace(/^- /, '').replace(/\(.*?\)/g, '').trim();
    };

    return (
      <motion.div 
        className="dashboard-alacarte-tab"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
      >
        <div className="filter-controls pinto-dash-filters">
          <div className="search-input-wrapper" style={{ flex: '1 1 100%' }}>
            <Search size={16} className="search-icon" />
            <input 
              type="text" 
              placeholder="ค้นหาชื่อเมนูสั่งแยก..." 
              value={searchQueryALaCarte}
              onChange={(e) => setSearchQueryALaCarte(e.target.value)}
              className="menu-search-input"
            />
          </div>
        </div>

        {ordersLoading ? (
          <div className="timeline-loading"><Skeleton height="80px" radius="16px" /><Skeleton height="80px" radius="16px" /></div>
        ) : filteredALaCarte.length > 0 ? (
          <div className="meal-history-list" style={{ marginTop: '16px' }}>
            {filteredALaCarte.map((item, idx) => {
              if (item._type === 'extra') {
                const ex = item;
                return (
                  <motion.div key={`extra-${ex.id || idx}`} className="meal-history-card" initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-20px"}} transition={{duration:0.4}}>
                    <div className="meal-card-content">
                      <div className="meal-card-header">
                        <div className="meal-title-group">
                          <span className="meal-name-main">{ex.menu_items?.name || 'สั่งอาหารแยก'} {ex.quantity > 1 && `(x${ex.quantity})`}</span>
                          <span className="category-badge brand-badge">A La Carte</span>
                          {ex.notes && ex.notes.includes('[ไม่รับข้าว]') && (
                            <span className="category-badge" style={{ backgroundColor: '#fee2e2', color: '#b91c1c' }}>
                              ไม่รับข้าว
                            </span>
                          )}
                        </div>
                        <span className={`delivery-status ${isScheduleCompleted(ex) ? 'success' : 'pending'}`}>
                          {isScheduleCompleted(ex) ? 'ส่งสำเร็จ' : 'รอจัดส่ง'}
                        </span>
                      </div>
                      <div className="meal-type-row">
                        <span className="meal-type-label">ปิ่นโตสั่งเพิ่ม</span>
                        <span className="delivery-date" style={{marginLeft: 'auto', fontSize: '12px'}}>{formatThaiDateShort(ex.delivery_date)}</span>
                      </div>
                      {ex.menu_items && (() => {
                        const adj = calculateAdjustedMacros(ex.menu_items, ex.notes);
                        return (
                          <div className="macros-row mt-2">
                            <span className="macro cal">{adj.cal} Kcal</span>
                            <span className="macro pro">P: {adj.pro}g</span>
                            <span className="macro carb">C: {adj.carb}g</span>
                            <span className="macro fat">F: {adj.fat}g</span>
                          </div>
                        );
                      })()}
                    </div>
                  </motion.div>
                );
              } else {
                const order = item;
                return (
                  <motion.div key={order.order_id || idx} className={`meal-history-card ${order.status === 'cancelled' ? 'cancelled-card' : ''}`} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-20px"}} transition={{duration:0.4}}>
                    <div className="meal-card-content">
                      <div className="meal-card-header">
                        <div className="meal-title-group">
                          <span className="meal-name-main">{getTitle(order.menu_name)}</span>
                          <span className="category-badge brand-badge">A La Carte</span>
                        </div>
                        <span className={`delivery-status ${order.delivery_status === 'ส่งสำเร็จ' ? 'success' : 'pending'}`}>
                          {order.status === 'cancelled' ? 'ยกเลิกแล้ว' : order.delivery_status || 'กำลังดำเนินการ'}
                        </span>
                      </div>
                      <div className="meal-type-row">
                        <span className="meal-type-label">#{order.display_order_id || order.order_id?.substring(0,8)}</span>
                        <span className="delivery-date" style={{marginLeft: 'auto', fontSize: '12px'}}>{formatThaiDateShort(order.delivery_date)}</span>
                      </div>
                      <div className="macros-row mt-2">
                        <span className="macro cal" style={{backgroundColor: 'var(--color-surface)'}}>฿{order.total_amount}</span>
                      </div>
                    </div>
                  </motion.div>
                );
              }
            })}
          </div>
        ) : (
          <EmptyState icon={<ShoppingBag size={48} />} title="ไม่มีออเดอร์ทั่วไป" description="ไม่พบรายการสั่งซื้อที่ตรงกับการค้นหา" />
        )}
      </motion.div>
    );
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-layout">
        
        {/* Sidebar Header */}
        <div className="dashboard-sidebar">
          <div className="greeting-section">
            <h1 className="greeting-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              สวัสดีคุณ{getFirstName(member?.full_name)} <Sparkles size={24} className="text-brand" />
            </h1>
            <p className="greeting-subtitle">ติดตามภาพรวมสุขภาพและมื้ออาหารของคุณได้ที่นี่</p>
          </div>

          <div className="date-filter-wrapper">
            <h4 className="filter-label">กรองข้อมูลตามช่วงเวลา</h4>
            <div className="date-filter-stack">
              <div className="date-input-container">
                <Calendar size={16} className="text-brand" />
                <input type="date" className="date-input-clean" value={dateRange.start} onChange={(e) => handleDateChange(e, 'start')}/>
              </div>
              <div className="date-input-container">
                <Calendar size={16} className="text-brand" />
                <input type="date" className="date-input-clean" value={dateRange.end} onChange={(e) => handleDateChange(e, 'end')}/>
              </div>
              <div className="date-filter-actions" style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                <button onClick={resetToAll} style={{ flex: 1, padding: '6px 8px', fontSize: '0.8rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer', color: 'var(--color-text-secondary)', fontWeight: '500', transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; e.currentTarget.style.color = 'var(--color-brand)'; }} onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text-secondary)'; }}>ทั้งหมด</button>
                <button onClick={resetToToday} style={{ flex: 1, padding: '6px 8px', fontSize: '0.8rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer', color: 'var(--color-text-secondary)', fontWeight: '500', transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; e.currentTarget.style.color = 'var(--color-brand)'; }} onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text-secondary)'; }}>วันนี้</button>
                <button onClick={resetToThisMonth} style={{ flex: 1, padding: '6px 8px', fontSize: '0.8rem', borderRadius: '8px', border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer', color: 'var(--color-text-secondary)', fontWeight: '500', transition: 'all 0.2s ease' }} onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--color-brand)'; e.currentTarget.style.color = 'var(--color-brand)'; }} onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text-secondary)'; }}>เดือนนี้</button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          
          {/* Custom Tabs */}
          <div className="dashboard-tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`dash-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              >
                {activeTab === tab.id && (
                  <motion.div layoutId="dash-tab-indicator" className="dash-tab-indicator" />
                )}
                <span className="dash-tab-label">{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="tab-content-container">
            <AnimatePresence mode="wait">
              {activeTab === 'overview' && <React.Fragment key="overview">{renderOverviewTab()}</React.Fragment>}
              {activeTab === 'pinto' && <React.Fragment key="pinto">{renderPintoTab()}</React.Fragment>}
              {activeTab === 'alacarte' && <React.Fragment key="alacarte">{renderALaCarteTab()}</React.Fragment>}
            </AnimatePresence>
          </div>

        </div>

      </div>
    </div>
  );
};

export default DashboardPage;
